import React from 'react';
import PropTypes from 'prop-types';

import notifier from '@albert-io/notifier';
import {
  addToast,
  Anchor,
  Banner,
  Button,
  Dialogue,
  InputGroup,
  Label,
  Radio,
  Modal,
  WithTooltip
} from '@albert-io/atomic';

import {getModelForResourceType} from 'resources/modelRegistry';
import ArrayToReadableFragment from 'lib/hocs/arrayToReadableFragment';

export default class SchoolAccountsBanners extends React.Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    school: PropTypes.instanceOf(getModelForResourceType('school_v5'))
  };

  constructor(props) {
    super(props);

    this.state = {
      school: props.school,
      showModal: false
    };
  }

  componentDidUpdate(prevProps, prevState) {
    // if the user does not click save after toggling options, reset state
    const modalWillUnmount = prevState.showModal === true && this.state.showModal === false;
    const hasUnsavedChanges =
      this.props.school.isRestrictClassroomEnrollment() !==
      this.state.school.isRestrictClassroomEnrollment();
    if (modalWillUnmount) {
      if (hasUnsavedChanges) {
        /* eslint-disable react/no-did-update-set-state */
        this.setState(() => ({
          school: this.props.school
        }));
      }
    }
  }

  handleChange = (e) => {
    e.persist();
    this.setState(({school}) => ({
      school: school.setRestrictClassroomEnrollment(e.target.value === 'true')
    }));
  };

  handleSave = async () => {
    try {
      await this.state.school.save();
      addToast({
        title: 'Success!',
        type: 'success',
        message: `Your changes were saved successfully.`
      });
      this.props.onChange();
      this.toggleModal();
    } catch (error) {
      addToast({
        title: 'Oops! Something went wrong',
        type: 'danger',
        message: 'There was a problem saving your changes. If the problem persists, contact us.'
      });
      notifier.notify(error, {
        component: 'Admin dashboard > school accounts',
        name: 'Failed to save enrollment restriction settings'
      });
    }
  };

  toggleModal = () =>
    this.setState(({showModal}) => ({
      showModal: !showModal
    }));

  render() {
    const {school} = this.props;
    const emailDomains = school.getEmailDomains();

    const showManagedAccountsBanner = emailDomains.size > 0;
    const isRestrictClassroomEnrollment = school.isRestrictClassroomEnrollment();
    return (
      <>
        {showManagedAccountsBanner && (
          <>
            <Banner className='u-mar-b_1'>
              <Banner.Icon icon={['far', 'school']} />
              <div className='u-mar-r_1'>
                Accounts ending in{' '}
                <ArrayToReadableFragment
                  arr={[...emailDomains.map((domain) => domain.getName())]}
                  as='strong'
                />{' '}
                are managed by {school.getName()}.
              </div>
              <WithTooltip
                placement='top'
                content='You can use domains to restrict student enrollment in classes and manage students.'
                focusable
                hideFocus
                wrapperProps={{className: 'u-mar-l_auto'}}
              >
                <Banner.Icon
                  className='school-accounts__tooltip-btn'
                  icon='question-circle'
                  iconStyle='regular'
                />
              </WithTooltip>
            </Banner>
            <Banner>
              <Banner.Icon icon={['far', 'user-plus']} />
              <div>
                {isRestrictClassroomEnrollment ? (
                  <p>Students can only enroll in classes by using a school-owned email address.</p>
                ) : (
                  <p>Students can enroll in classes by using any email address.</p>
                )}
                <Anchor
                  as='span'
                  role='button'
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      this.toggleModal();
                    }
                  }}
                  onClick={this.toggleModal}
                >
                  Edit settings
                </Anchor>
              </div>
            </Banner>
          </>
        )}
        {this.state.showModal && (
          <Modal ariaLabel='Student Enrollment Settings' handleClose={this.toggleModal}>
            {({CloseButtonWrapper}) => (
              <Dialogue title='Student Enrollment Settings' handleClose={this.toggleModal} inModal>
                <Dialogue.Body>
                  <InputGroup className='u-mar-b_2' direction='column'>
                    <Label>
                      <Radio
                        checked={!this.state.school.isRestrictClassroomEnrollment()}
                        className='u-mar-r_2'
                        name='restrict-enrollment'
                        onChange={this.handleChange}
                        value='false'
                      />
                      Allow students to use any email address.
                    </Label>
                    <Label>
                      <Radio
                        checked={this.state.school.isRestrictClassroomEnrollment()}
                        className='u-mar-r_2'
                        name='restrict-enrollment'
                        onChange={this.handleChange}
                        value='true'
                      />
                      Restrict student enrollment to your school-owned email domains.
                    </Label>
                  </InputGroup>
                </Dialogue.Body>
                <Dialogue.BtnGroup>
                  <CloseButtonWrapper>
                    <Button color='secondary'>Cancel</Button>
                  </CloseButtonWrapper>
                  <Button
                    disabled={this.state.school === this.props.school}
                    onClick={this.handleSave}
                  >
                    Save
                  </Button>
                </Dialogue.BtnGroup>
              </Dialogue>
            )}
          </Modal>
        )}
      </>
    );
  }
}
